// THE SIGN-OFF STAGE: the building as a generated isometric, split by level, area and
// system (owner 2026-11-05) - the Project at a Glance drawing, in the sign-off's colours,
// in place of the 3D viewer and the tiles.
//
// One plate per level, bottom first, lifted in the same oblique projection the glance
// stack uses (glanceModel.makeStackProjector), so a plate here and a plate there read as
// the same building. On each plate:
//
//   the level's footprint   pale, from the tile set: every cell that holds a placed element
//   each drawn area         filled in its sign-off colour on the date the page is scrolled
//                           to - grey, blue (owner/GC) or gold (contractor) - and clickable
//   the systems picked      the glance's own bucket paths for the drawn layers, and the
//                           occupied cells for a model system, clipped to the areas picked
//
// Nothing here is sample data: a level, an area or a system that the export and the
// drawings do not carry is simply not drawn, and the panel beside it says so.
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { bucketKey, dotsPath, linesPath, makeStackProjector, marksPath, polysPath } from "components/Coordination/Glance/glanceModel";
import { COLORS, GREY } from "./signoffState";
import { cellBounds, cellsCentre, cellsOn, cellsPath, drawnLayer, hasSystem, inArea, isLayer, layerIdOf } from "./isoModel";

// the glance level id ("L1") for a sign-off level key: the same rule the server places
// elements with (utils/elementAreas.levelKey), so the two halves meet on the name
export function glanceLevelOf(manifest, levelKey, levelName) {
  const want = norm(levelKey);
  const ls = (manifest && manifest.levels) || [];
  const byId = ls.find((l) => norm(l.id) === want);
  if (byId) return byId.id;
  const m = /^L(\d+)$/.exec(want);
  const byName = ls.find((l) => {
    const n = norm(l.name);
    if (n === want) return true;
    return !!m && (n === `LEVEL${m[1]}` || n === `L${m[1]}` || n === `FLOOR${m[1]}`);
  });
  if (byName) return byName.id;
  return norm(levelName) && ls.find((l) => norm(l.name) === norm(levelName)) ? ls.find((l) => norm(l.name) === norm(levelName)).id : "";
}

const norm = (s) => String(s || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * levelKey -> name, the timeline's names first. A level only the model is on has no name
 * there (the names come from the areas drawn on each level), and read "L2" on the drawing;
 * the glance manifest knows what that floor is called, so it fills the gaps.
 */
export function levelNamesFrom(levels, manifest) {
  const out = { ...(levels || {}) };
  for (const l of (manifest && manifest.levels) || []) {
    const k = norm(l.id);
    if (k && !out[k] && l.name) out[k] = l.name;
  }
  return out;
}

const f1 = (v) => v.toFixed(1);

// The colour tokens the drawing uses. Exporting the SVG on its own takes them with it,
// read from what the page actually computed - the stylesheet stays the one definition.
const TOKENS = ["--paper", "--sheet", "--ink", "--ink2", "--line", "--room", "--system", "--systemink",
  "--tel", "--sec", "--pwr", "--ltg", "--fire", "--duct", "--pipe", "--under", "--proc", "--rtutop"];

/** The drawing as a standalone SVG data URL, with its tokens resolved. */
export function svgDataUrl(svg, host) {
  if (!svg) return "";
  const css = window.getComputedStyle(host || svg);
  const vars = TOKENS.map((t) => `${t}:${(css.getPropertyValue(t) || "").trim() || "#888"}`).join(";");
  const copy = svg.cloneNode(true);
  copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  style.textContent = `svg{${vars};background:${(css.getPropertyValue("--sheet") || "#fff").trim()}}`;
  copy.insertBefore(style, copy.firstChild);
  // outerHTML where XMLSerializer is not there (jsdom); both give the same markup for SVG
  const xml = typeof XMLSerializer === "function" ? new XMLSerializer().serializeToString(copy) : copy.outerHTML;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
}

/** One drawn layer over a level: its glance buckets, in the layer's colour. */
function LayerPaths({ manifest, glanceLevel, layerId, project, rank, clipId }) {
  const paths = useMemo(() => {
    const buckets = (manifest && manifest.buckets) || {};
    const zones = ((manifest && manifest.zones) || []).map((z) => z.index);
    const keys = (zones.length ? zones : [0]).map((z) => bucketKey(glanceLevel, layerId, z));
    return keys.map((k) => buckets[k]).filter(Boolean);
  }, [manifest, glanceLevel, layerId]);
  if (!paths.length) return null;
  const l = drawnLayer(layerId);
  const color = `var(${(l && l.token) || "--system"})`;
  const proj = (x, y) => project(x, y, rank);
  return (
    <g clipPath={clipId ? `url(#${clipId})` : undefined} style={{ pointerEvents: "none" }} data-testid={`iso-layer-${layerId}`}>
      {paths.map((b, i) => (
        <g key={i}>
          {b.lines ? <path d={linesPath(b.lines, proj)} stroke={color} strokeWidth="0.9" fill="none" /> : null}
          {b.polys ? <path d={polysPath(b.polys, proj)} fill={color} fillOpacity="0.55" stroke={color} strokeWidth="0.4" /> : null}
          {b.dots ? <path d={dotsPath(b.dots, proj, 1)} fill={color} /> : null}
          {b.marks ? <path d={marksPath(b.marks, proj, 3)} fill={color} stroke="var(--sheet)" strokeWidth="0.5" /> : null}
        </g>
      ))}
    </g>
  );
}

export default function SignoffIsometric({
  tiles, levels, areas, manifest = null, states, scope,
  onToggleArea = null, onToggleLevel = null, onPickArea = null, selectedAreaId = "",
  focusKeys = null, height = 560, captureRef = null,
}) {
  // the page's "Download snapshot" asks for a function, not the node
  const hostRef = useRef(null);
  const svgRef = useRef(null);
  useEffect(() => {
    if (!captureRef) return undefined;
    captureRef.current = () => svgDataUrl(svgRef.current, hostRef.current);
    return () => { captureRef.current = null; };
  }, [captureRef]);

  const grid = tiles && tiles.grid;
  const list = (tiles && tiles.tiles) || [];
  const pickedAreas = useMemo(() => new Set(scope.areaIds || []), [scope.areaIds]);
  const pickedLevels = useMemo(() => new Set(scope.levelKeys || []), [scope.levelKeys]);
  const layerIds = useMemo(() => (scope.systems || []).filter(isLayer).map(layerIdOf), [scope.systems]);
  const modelSystems = useMemo(() => (scope.systems || []).filter((s) => !isLayer(s)), [scope.systems]);

  // every level's cells, and the areas drawn on it
  // only the CURRENT areas are drawn: a superseded revision of an area covers the same
  // ground as the one that replaced it, and both would be painted on the same plate
  const drawnAreas = useMemo(() => Object.keys(areas || {}).filter((id) => areas[id] && areas[id].current !== false), [areas]);
  const plates = useMemo(() => levels.map((l) => {
    const all = cellsOn(list, l.key);
    const byArea = new Map();
    for (const id of drawnAreas) {
      const cells = cellsOn(list, l.key, inArea(id));
      if (cells.size) byArea.set(id, cells);
    }
    return { ...l, cells: all, byArea };
  }), [levels, list, drawnAreas]);

  const b = useMemo(() => {
    const keys = new Set();
    plates.forEach((p) => p.cells.forEach((_, k) => keys.add(k)));
    return cellBounds(keys, grid);
  }, [plates, grid]);
  const { project, width, height: svgH } = useMemo(
    () => makeStackProjector(b, Math.max(1, plates.length), { width: 1140 }), [b, plates.length],
  );

  // a target's sign-off colour on the date the page is on
  const stateOf = useCallback((key) => states.get(key) || GREY, [states]);
  // the ring's tiles, PER LEVEL: a tile is a cube, and the cell above it on the next plate
  // is a different tile - outlining by `i,j` alone marked the whole column
  const focusCells = useMemo(() => {
    if (!focusKeys) return null;
    const want = new Set(focusKeys);
    const out = new Map();
    for (const t of list) {
      if (!want.has(t.key)) continue;
      for (const g of t.g || []) {
        if (!g.l) continue;
        if (!out.has(g.l)) out.set(g.l, new Set());
        out.get(g.l).add(`${t.i},${t.j}`);
      }
    }
    return out;
  }, [focusKeys, list]);

  // "Show the scope alone": a level with nothing picked on it is left out, and an area
  // that is not picked is drawn faintly rather than in its sign-off colour
  const anyPicked = pickedAreas.size > 0 || pickedLevels.size > 0;
  const only = !!scope.only && anyPicked;
  const shown = only
    ? plates.filter((p) => pickedLevels.has(p.key) || [...p.byArea.keys()].some((id) => pickedAreas.has(id)))
    : plates;

  if (!plates.length) return null;
  if (!shown.length) return (
    <div className="signoff-iso" data-testid="signoff-iso">
      <p className="signoff-muted" style={{ padding: 12 }}>Nothing in the scope is drawn on this model yet.</p>
    </div>
  );
  return (
    <div className="signoff-iso" style={{ maxHeight: height, overflow: "auto" }} data-testid="signoff-iso" ref={hostRef}>
      <svg viewBox={`0 0 ${width} ${svgH}`} width="100%" style={{ display: "block" }} ref={svgRef} role="img"
           aria-label="The building by level, area and system, coloured by sign-off">
        <defs>
          {shown.map((p) => {
            // the systems picked are drawn only inside the areas picked on this level
            const keys = new Set();
            const mine = [...p.byArea.keys()].filter((id) => pickedAreas.has(id));
            mine.forEach((id) => p.byArea.get(id).forEach((_, k) => keys.add(k)));
            if (!keys.size) return null;
            return <clipPath key={p.key} id={`iso-clip-${p.key}`}><path d={cellsPath(keys, grid, project, p.rank)} /></clipPath>;
          })}
        </defs>
        {plates.map((p) => {
          const gl = glanceLevelOf(manifest, p.key, p.name);
          const clipId = [...p.byArea.keys()].some((id) => pickedAreas.has(id)) ? `iso-clip-${p.key}` : "";
          const onLevel = pickedLevels.has(p.key);
          const label = cellsCentre(p.cells, grid, project, p.rank);
          return (
            <g key={p.key} data-testid={`iso-level-${p.key}`}>
              {/* the level's footprint */}
              <path d={cellsPath(p.cells, grid, project, p.rank)} fill="var(--room)" fillOpacity={onLevel ? 0.95 : 0.6}
                    stroke="var(--line)" strokeWidth="0.4" />
              {/* each drawn area in its sign-off colour */}
              {[...p.byArea.entries()].map(([id, cells]) => {
                const st = Math.max(stateOf(`area|${id}`), stateOf(`level|${p.key}`));
                const on = pickedAreas.has(id) || (pickedLevels.has(p.key) && !pickedAreas.size);
                const sel = selectedAreaId === id;
                const faint = only && !on;
                return (
                  <path key={id} d={cellsPath(cells, grid, project, p.rank)}
                        fill={faint ? "var(--room)" : COLORS[st]} fillOpacity={faint ? 0.25 : on ? 0.85 : st > GREY ? 0.6 : 0.3}
                        stroke={on || sel ? "#e8590c" : "var(--ink2)"} strokeWidth={on || sel ? 1.6 : 0.5}
                        style={{ cursor: onToggleArea ? "pointer" : "default" }}
                        data-testid={`iso-area-${id}`}
                        onClick={(e) => { if (e.shiftKey && onToggleArea) onToggleArea(id); else if (onPickArea) onPickArea(id); }}>
                    <title>{`${(areas[id] && areas[id].name) || "Area"} · ${p.name} — click for its sign-off, shift-click to put it in the scope`}</title>
                  </path>
                );
              })}
              {/* the ring's tiles, outlined */}
              {focusCells && focusCells.get(p.key) ? (
                <path d={cellsPath([...focusCells.get(p.key)], grid, project, p.rank)}
                      fill="none" stroke="#111827" strokeWidth="1.2" strokeDasharray="3 2" style={{ pointerEvents: "none" }}
                      data-testid={`iso-focus-${p.key}`} />
              ) : null}
              {/* the model systems picked: the cells that carry them */}
              {modelSystems.map((s, i) => {
                const cells = cellsOn(list, p.key, (g) => hasSystem(s)(g) && (!pickedAreas.size || [...pickedAreas].some((a) => inArea(a)(g))));
                if (!cells.size) return null;
                return <path key={s} d={cellsPath(cells, grid, project, p.rank)} fill="none" stroke="var(--system)" strokeWidth={1 + i * 0.2}
                             strokeOpacity="0.9" style={{ pointerEvents: "none" }} data-testid={`iso-system-${p.key}`} />;
              })}
              {/* the drawn layers picked: the glance's own geometry */}
              {gl ? layerIds.map((id) => (
                <LayerPaths key={id} manifest={manifest} glanceLevel={gl} layerId={id} project={project} rank={p.rank} clipId={clipId} />
              )) : null}
              <text x={f1(label[0])} y={f1(label[1])} textAnchor="middle" fontSize="15" fontWeight="600" fill="var(--ink)"
                    paintOrder="stroke" stroke="var(--sheet)" strokeWidth="4" data-testid={`iso-level-name-${p.key}`}
                    style={{ cursor: onToggleLevel ? "pointer" : "default" }}
                    onClick={() => { if (onToggleLevel) onToggleLevel(p.key); }}>
                {p.name}
                <title>{`${p.name} - ${p.tiles.toLocaleString()} tiles, ${p.elements.toLocaleString()} placed elements. Click to put the whole level in the scope.`}</title>
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
